import { Box, styled } from '@mui/material';
import { IPublicAndPrivateSettings } from '@pepperhq/location-sdk';
import { MuiCardForm } from 'lib/form/MuiCardForm';
import { SettingDisplay } from 'lib/SettingDisplay';
import { SwitchFormField } from 'lib/form/fields/SwitchFormField';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { PasswordFormField } from 'lib/form/fields/PasswordFormField';
import { MultiSelectFormField } from 'lib/form/fields/MultiSelectFormField';
import { getActionTypeOptions } from 'components/actions/utils';
import { SettingDisplayPassword } from '../SettingDisplayPassword';
import { getSelectedOptions } from 'components/utils/getSelectedOptions';
import { SettingDisplaySwitch } from '../SettingDisplaySwitch';
import { SettingDisplayText } from '../SettingDisplayText';

type IProps = {
    settings: IPublicAndPrivateSettings;
    onSubmit: (values: Record<string, any>) => Promise<boolean>;
};

const PREFIX = 'MarketingSettings';

const classes = {
    settingsCard: `${PREFIX}-settingsCard`,
    displayClassName: `${PREFIX}-displayClassName`
};

const StyledMuiCardForm = styled(MuiCardForm)(({ theme }) => ({
    [`&.${classes.settingsCard}`]: {
        marginBottom: theme.spacing(2),
        overflowY: 'visible' // Needed for the dropdowns
    },
    [`& .${classes.displayClassName}`]: {
        padding: theme.spacing(2)
    }
}));

// Do we just leave subscriptionsEnabled as true so they can be removed in the case someone has them active and then stops using subscriptions?
const bloomreachEnabledEventsOptions = getActionTypeOptions({
    includeNoneOption: false,
    subscriptionsEnabled: true
});

export function BloomreachSettingsCard({ settings, onSubmit }: IProps) {
    return (
        <StyledMuiCardForm
            title="Bloomreach"
            initialValues={{
                bloomreachEnabled: settings?.bloomreachEnabled,
                bloomreachProjectToken: settings?.bloomreach?.projectToken,
                bloomreachApiKeyId: settings?.bloomreach?.apiKeyId,
                bloomreachApiSecret: settings?.bloomreach?.apiSecret,
                bloomreachBaseUrl: settings?.bloomreach?.baseUrl,
                bloomreachEmailIdFieldName: settings?.bloomreach?.emailIdFieldName,
                bloomreachEnabledEvents: getSelectedOptions(
                    bloomreachEnabledEventsOptions,
                    settings?.bloomreach?.enabledEvents ?? []
                ),
                bloomreachEnableSegmentAttribute: settings?.bloomreach?.enableSegmentAttribute
            }}
            onSubmit={onSubmit}
            childrenWhileDisplaying={
                <Box display="flex" flexDirection="column">
                    <SettingDisplaySwitch title="Bloomreach Enabled" checked={settings?.bloomreachEnabled} />
                    <SettingDisplayText title="Token" text={settings?.bloomreach?.projectToken} />
                    <SettingDisplayText title="API Key ID" text={settings?.bloomreach?.apiKeyId} />
                    <SettingDisplayPassword title="API Secret" text={settings?.bloomreach?.apiSecret} />
                    <SettingDisplayText title="Base URL" text={settings?.bloomreach?.baseUrl} />
                    <SettingDisplayText
                        title="Email ID Field Name"
                        text={settings?.bloomreach?.emailIdFieldName}
                    />
                    <SettingDisplayText
                        title="Enabled Events"
                        text={settings?.bloomreach?.enabledEvents
                            ?.map(v => bloomreachEnabledEventsOptions.find(o => o.value === v)?.label ?? v)
                            .join(', ')}
                    />
                    <SettingDisplaySwitch
                        title="Enable Segment Attribute"
                        checked={settings?.bloomreach?.enableSegmentAttribute}
                    />
                </Box>
            }
            childrenWhileEditing={
                <Box display="flex" flexDirection="column">
                    <SettingDisplay label="Bloomreach Enabled">
                        <SwitchFormField name="bloomreachEnabled" />
                    </SettingDisplay>
                    <SettingDisplay label="Project Token">
                        <TextFormField name="bloomreachProjectToken" />
                    </SettingDisplay>
                    <SettingDisplay label="API Key ID">
                        <TextFormField name="bloomreachApiKeyId" />
                    </SettingDisplay>
                    <SettingDisplay label="API Secret">
                        <PasswordFormField name="bloomreachApiSecret" />
                    </SettingDisplay>
                    <SettingDisplay label="Base URL">
                        <TextFormField name="bloomreachBaseUrl" />
                    </SettingDisplay>
                    <SettingDisplay label="Email ID Field Name">
                        <TextFormField name="bloomreachEmailIdFieldName" />
                    </SettingDisplay>
                    <SettingDisplay label="Enabled Events">
                        <Box flexGrow={1}>
                            <MultiSelectFormField
                                name="bloomreachEnabledEvents"
                                options={bloomreachEnabledEventsOptions}
                                isMulti
                            />
                        </Box>
                    </SettingDisplay>
                    <SettingDisplay label="Enable Segment Attribute">
                        <SwitchFormField name="bloomreachEnableSegmentAttribute" />
                    </SettingDisplay>
                </Box>
            }
            className={classes.settingsCard}
            boxClassName={classes.displayClassName}
        />
    );
}
